import {
  useObservationRequest,
  useCreateObservationRequest,
  useCancelObservationRequest,
} from '@/api/practicalObservations';

type Props = {
  enrollmentId: number;
  lessonId: number;
  isLocked: boolean;
};

export function ObservationLessonState({ enrollmentId, lessonId, isLocked }: Props) {
  const { data: response, isLoading } = useObservationRequest(enrollmentId, lessonId);
  const create = useCreateObservationRequest();
  const cancel = useCancelObservationRequest();

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Cargando estado…</p>;
  }

  const request = response?.data ?? null;

  if (!request || request.status === 'cancelled') {
    return (
      <div className="rounded-md border border-border bg-card p-3">
        <p className="text-sm text-card-foreground">
          Esta lección requiere observación presencial por un instructor.
          {request?.status === 'cancelled' && (
            <span className="mt-1 block text-xs text-muted-foreground">
              Solicitud anterior cancelada — puede solicitar de nuevo.
            </span>
          )}
        </p>
        <button
          type="button"
          disabled={isLocked || create.isPending}
          onClick={() => create.mutate({ enrollmentId, lessonId })}
          className="mt-2 rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {create.isPending ? 'Solicitando…' : 'Solicitar observación'}
        </button>
        {isLocked && (
          <p className="mt-1 text-xs text-muted-foreground">
            Complete las lecciones anteriores primero.
          </p>
        )}
        {create.error && (
          <p className="mt-1 text-xs text-destructive">
            {create.error instanceof Error
              ? create.error.message
              : 'No se pudo crear la solicitud.'}
          </p>
        )}
      </div>
    );
  }

  if (request.status === 'pending') {
    return (
      <div className="rounded-md border border-amber-500/40 bg-card p-3">
        <p className="text-sm text-card-foreground">
          <span className="inline-flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400">
            ⏳ Esperando observación del instructor
          </span>{' '}
          <span className="text-xs text-muted-foreground">
            (Solicitada el {new Date(request.requested_at).toLocaleDateString()})
          </span>
        </p>
        <button
          type="button"
          disabled={cancel.isPending}
          onClick={() => {
            if (confirm('¿Cancelar la solicitud de observación?')) {
              cancel.mutate({ id: request.id });
            }
          }}
          className="mt-2 rounded border border-border px-3 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50"
        >
          Cancelar solicitud
        </button>
      </div>
    );
  }

  if (request.status === 'completed') {
    return (
      <div className="rounded-md border border-emerald-500/40 bg-card p-3">
        <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
          ✓ Observación completada
          {request.completed_at && ` el ${new Date(request.completed_at).toLocaleDateString()}`}.
        </p>
        {request.criterion_results.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-medium text-card-foreground">
              Ver resultados ({request.criterion_results.length} criterios)
            </summary>
            <ul className="mt-2 space-y-1 text-xs">
              {request.criterion_results.map((r) => (
                <li key={r.criterion_id} className="flex items-start gap-2">
                  <span
                    className={
                      r.passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'
                    }
                  >
                    {r.passed ? '✓' : '✗'}
                  </span>
                  <span className="flex-1 text-card-foreground">
                    {r.criterion_label}
                    {r.notes && (
                      <span className="ml-1 italic text-muted-foreground">— {r.notes}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {request.evaluator_notes && (
              <p className="mt-2 text-xs italic text-muted-foreground">
                Nota del evaluador: {request.evaluator_notes}
              </p>
            )}
            {request.evidence.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-card-foreground">Evidencia registrada:</p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {request.evidence.map((ev) => (
                    <li key={ev.id} className="font-mono text-muted-foreground">
                      {ev.file_name}{' '}
                      <span className="text-muted-foreground/70">({ev.evidence_type})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </details>
        )}
      </div>
    );
  }

  return null;
}
